import { useEffect, useState } from 'react'
import { api, type Principal } from './lib/api'
import { SignIn } from './pages/SignIn'
import { Shell } from './pages/Shell'
import { AdminApp } from './admin/AdminApp'

/** Which of the two apps is this?
 *
 *  In production the super admin console has its own origin --
 *  admin.crm.advancetalk.ca -- so the browser keeps its cookie away from every
 *  customer's site. The hostname is therefore what decides, not the path.
 *
 *  The /admin path is kept as a second way in because localhost and a bare IP
 *  have no subdomain to key off, and the server falls back to serving both
 *  apps there for exactly the same reason.
 *
 *  The label below must match ADMIN_SUBDOMAIN on the server. If they disagree,
 *  this renders the console on a host whose API routes answer 404 -- which is
 *  precisely the bug this comment exists to prevent happening twice.
 */
const ADMIN_SUBDOMAIN = 'admin'

function isAdminEntry(): boolean {
  const { hostname, pathname } = window.location
  return (
    hostname.split('.')[0].toLowerCase() === ADMIN_SUBDOMAIN ||
    pathname.startsWith('/admin')
  )
}

export function App() {
  // Chosen once, at the top, so neither app calls a hook conditionally. The
  // previous version returned <AdminApp /> before useState ran, which happened
  // to work only because the condition never changed between renders.
  return isAdminEntry() ? <AdminApp /> : <TenantApp />
}

function TenantApp() {
  const [me, setMe] = useState<Principal | null>(null)
  const [checked, setChecked] = useState(false)

  // A refresh must not sign the dispatcher out. The cookie is the session; ask
  // the server who it belongs to.
  useEffect(() => {
    api
      .whoami()
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setChecked(true))
  }, [])

  if (!checked) return null
  if (!me) return <SignIn onSignedIn={setMe} />
  // ?panel=1 is the pop-out window: the same app showing the panel and nothing
  // else, so it can sit on a second display. While it is open the main window
  // stands its own panel down, keeping exactly one session.
  const panelOnly = new URLSearchParams(window.location.search).get('panel') === '1'
  return <Shell me={me} onSignedOut={() => setMe(null)} panelOnly={panelOnly} />
}
